import { Injectable } from '@angular/core';
import { ResourceData } from '../elements/resource/resource.types';
import { CanvasService } from '../services/canvas/canvas.service';
import {
  __Animation,
  _SVGAttributes,
  FillConfig,
  StrokeConfig,
  SVGAttributes,
  TextConfig,
} from '../elements/resource/types/shape.type';
import { ActivatedRoute, Router } from '@angular/router';
import {
  saveResourceSuccess,
  setShowComponentSearch,
} from './store/editor.actions';
import { HttpService } from '../store/http/http.service';
import { RequestType } from '../store/http/http.service';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { OverlayConfig } from '../projects/project.reducer';

@Injectable()
export class EditorService {
  currentProject: ResourceData;
  overlayConfig: OverlayConfig;

  constructor(
    public readonly router: Router,
    private activatedRoute: ActivatedRoute,
    private readonly cs: CanvasService,
    private readonly http: HttpService,
  ) {
    // this.cs.keyEventSubscribe('Shift+d', () => {
    //   this.showComponentSearch();
    // });

    this.cs.generalEventSubscribe('globalClick', () => {
      this.overlayConfig = null;
      // console.log('eservice.globalClick');
    });

    this.cs.generalEventSubscribe('route-update', queryParams => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: '',
      });
    });
  }

  deleteComponent(ID: string): Observable<ResourceData> {
    return this.http.requestCall({
      path: `shape-v2/component/${ID}`,
      type: RequestType.DELETE,
    });
  }

  loadProjectWithFiles(
    projectId: string,
    // orgID: string,
  ): Observable<ResourceData> {
    return this.http.requestCall({
      path: `project/files/${projectId}/new`,
      type: RequestType.GET,
      headers: {
        //'organisation-id': orgID,
      },
    });
  }

  loadProjectOverview(
    projectId: string,
    // orgID: string,
  ): Observable<{
    project: ResourceData;
    files: ResourceData[];
    organisation: ResourceData;
  }> {
    return this.http
      .requestCall({
        path: `project/files/${projectId}/overview`,
        type: RequestType.GET,
        headers: {
          //'organisation-id': orgID,
        },
      })
      .pipe(
        map(data => ({
          files: data.relationships.files,
          project: data,
          organisation: data.relationships.organisation,
        })),
      );
    // .pipe(
    //   map(({ project, files }) => [
    //     setProjectInput({ project, files }),
    //     setProjectPreviewIsLoading({ value: false }),
    //   ]),
    // );
  }
  loadFiles(fileID: string, subSceneLoad = true) {
    subSceneLoad = true;
    return this.http
      .requestCall({
        type: RequestType.GET,
        path: `shape-v2/file/${fileID}`,
        params: subSceneLoad
          ? {
              // mode: 'subscene-load',
              // mode: 'subSceneLoad',
              // subSceneLoad,
            }
          : undefined,
      })
      .pipe(
        map(({ file, shapes, files, components }) => ({
          file,
          files,
          components,
          shapes: Object.values(shapes as Record<string, ResourceData>).reduce(
            (object, resource) => {
              object[resource.IRI as string] = this.mapShape(resource);
              return object;
            },
            {},
          ) as Record<string, ResourceData>,
        })),
      );
  }

  mapShape(data: any) {
    const descriptor = data.literals.descriptor;
    descriptor.svgAttributes = this.getSVGAttributes(descriptor.svgAttributes);

    // descriptor._any;
    descriptor.animations ||= Object.entries(
      descriptor._animationsByKey || ({} as Record<string, __Animation>),
    ).reduce((object, [id, value]: [string, __Animation]) => {
      object[id] = value.reduce((obj, val) => {
        obj[val.key] = val;
        return obj;
      }, {});
      return object;
    }, {});

    delete descriptor._animationsByKey;

    if (descriptor.type == 'text-shape' && !descriptor.textConfig) {
      descriptor.textConfig = {
        text: descriptor.text,
        fontFamily: descriptor.fontFamily,
        fontSize: descriptor.fontSize,
      } as TextConfig;
      delete descriptor.text;
    }

    if (descriptor.position) {
      descriptor.position.translate ||= {
        x: descriptor.position.x,
        y: descriptor.position.y,
      };
    }

    data.literals.descriptor = descriptor;
    return data;
  }

  generateImage(data: FormData) {
    return this.http.requestBlob(data).pipe(
      catchError(error => {
        console.log('------ error ------', error);
        return of(error);
      }),
    );
  }

  getSVGAttributes = (svgAttributes: _SVGAttributes) => {
    let fill: FillConfig;

    if (svgAttributes?.fill) {
      fill =
        typeof svgAttributes.fill == 'object'
          ? (svgAttributes.fill as FillConfig)
          : {
              color: svgAttributes.fill,
              gradient: svgAttributes.gradient,
              gradientDirection: svgAttributes.gradientDirection,
            };
    }

    let stroke: StrokeConfig;

    if (svgAttributes?.stroke) {
      stroke =
        typeof svgAttributes.stroke == 'object'
          ? (svgAttributes.stroke as StrokeConfig)
          : {
              color: svgAttributes.stroke,
              width: svgAttributes?.['stroke-width'],
              dash: svgAttributes.dash,
            };
    }

    return {
      ...(svgAttributes || {}),
      stroke,
      fill,
    };
  };

  saveResources(params: {
    projectIRI?: string;
    posts: ResourceData[];
    patches: ResourceData[];
    deletes: ResourceData[];
  }) {
    const { projectIRI, posts, patches, deletes } = params;
    return this.http
      .requestCall({
        type: RequestType.PATCH,
        path: 'shape',
        body: {
          project: { IRI: projectIRI },
          file: {},
          posts,
          patches,
          deletes,
        },
      })
      .pipe(map(() => saveResourceSuccess()));
  }

  getBackSVGAttributes(svgAttributes: SVGAttributes) {
    const stroke = svgAttributes?.stroke;
    const fill = svgAttributes?.fill;
    return {
      // ...(svgAttributes || {}),
      stroke: stroke?.color,
      'stroke-width': stroke?.width,
      dash: stroke?.dash,
      fill: fill?.color,
      gradient: fill?.gradient,
      gradientColor: fill?.gradientColor,
      gradientDirection: fill?.gradientDirection,
    };
  }

  showComponentSearch() {
    // Because the component search absorbs all keyevents
    this.cs.keyUp('d');
    this.cs.keyUp('Shift');
    console.log('show-component-search');
    this.cs.store.dispatch(setShowComponentSearch({ value: true }));
  }

  hideComponentSearch() {
    this.cs.store.dispatch(setShowComponentSearch({ value: false }));
  }
  async openStateEditor() {
    console.log('open-state-editor');

    // this.dialog
    //   .open(StateEditorComponent, {
    //     // data: ['planes', 'passengers', 'add-ons', 'payment'],
    //     data: this.previewShape.descriptor.states || [],
    //     hasBackdrop: true,
    //     disableClose: true,
    //   })
    //   .afterClosed()
    //   .subscribe(async (result?: string[]) => {
    //     if (result) {
    //       // const animationFrame =  this.updateRootAnimationFrame(result);
    //       // this.previewShape.descriptor.animationFrame = animationFrame;
    //       // this.previewShape.animationFrame = new MainAnimationFrameObject(this.previewShape, animationFrame);
    //       // console.log('--------- save:state ---------', result);
    //       this.previewShape.descriptor.states = result;
    //       this.previewShape.save();
    //     }
    //   });
  }
}
